'use client'

import * as React from 'react'
import { createContainer } from 'unstated-next'

import { Alert } from '../../../../../../brunswick/components/Alert/index'
import { useJoinVariantWaitlist } from '../../../../../_hooks/useJoinVariantWaitlist'
import { useTranslation } from '../../../../../_providers/TranslationProvider.client'
import {
  NextDialog,
  NextDialogProvider,
  NextDialogTitle,
} from '../../../../dialogs/NextDialog.client'

import { WaitlistForm } from './WaitlistForm/WaitlistForm'
import { Divider, Instructions, ProductTitle, WaitlistHeader } from './WaitlistModal.styles'

const WaitlistModal = () => {
  const {
    waitlist: waitlistCopy,
    magnolia: { general },
  } = useTranslation()

  const {
    closeWaitlistModal,
    errorMessage,
    modalTitle,
    shouldDisplayWaitlistModal,
    successMessage,
  } = useWaitlistModal()

  return (
    <NextDialogProvider>
      <NextDialog
        analyticsTitle='Waitlist'
        variant='default'
        onClose={closeWaitlistModal}
        open={shouldDisplayWaitlistModal}
      >
        <WaitlistHeader closeText={general.close}>
          <NextDialogTitle>{waitlistCopy.modal.headline}</NextDialogTitle>
        </WaitlistHeader>
        <div style={{ textAlign: 'left' }}>
          <Instructions>{waitlistCopy.modal.instructions}</Instructions>
          <Divider />
          <ProductTitle textType='h2'>{modalTitle}</ProductTitle>
          {!successMessage && <WaitlistForm />}
          {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
          {successMessage && <Alert severity='success'>{successMessage}</Alert>}
        </div>
      </NextDialog>
    </NextDialogProvider>
  )
}

export interface UseWaitlistModalProps {
  colorDisplayValue: string
  variantSku: string
  productTitle: string
  variantShopifyId: string | undefined | null
}

function useWaitlistModalImpl(props: UseWaitlistModalProps | undefined) {
  const { joinVariantWaitlist } = useJoinVariantWaitlist()
  const { variantSku, productTitle, variantShopifyId, colorDisplayValue } = props!
  const [shouldDisplayWaitlistModal, setShouldDisplayWaitlistModal] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)

  function openWaitlistModal(): void {
    setShouldDisplayWaitlistModal(true)
  }

  function clearMessages(): void {
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  function closeWaitlistModal(): void {
    clearMessages()
    setShouldDisplayWaitlistModal(false)
  }

  return {
    clearMessages,
    closeWaitlistModal,
    errorMessage,
    openWaitlistModal,
    variantSku,
    modalTitle: `${colorDisplayValue} ${productTitle}`,
    variantShopifyId,
    sendWaitlistRequest: joinVariantWaitlist,
    setErrorMessage,
    setSuccessMessage,
    shouldDisplayWaitlistModal,
    successMessage,
  }
}

const WaitlistModalContainer = createContainer(useWaitlistModalImpl)
export const useWaitlistModal = WaitlistModalContainer.useContainer
export const WaitlistModalProvider = ({
  children,
  ...rest
}: UseWaitlistModalProps & { children: React.ReactNode }) => {
  return (
    <WaitlistModalContainer.Provider initialState={rest}>
      <WaitlistModal />
      {children}
    </WaitlistModalContainer.Provider>
  )
}
